import { useEffect, useImperativeHandle, useMemo, type Ref } from 'react';
import * as Yup from 'yup';
import { type FieldData, useForm, MaterialSymbol, TextField, sanitizeToNumeric } from 'vopay';
import { useTranslation } from 'react-i18next';
import TierCheckbox from './TierCheckbox';
import TierBadge from './TierBadge';
import styles from './ApprovalTierRow.module.scss';
import type { ApprovalTier } from './ApprovalTiers';

type TierValues = {
  startAmount: string;
  endAmount: string;
  approversNeeded: string;
};

export type ApprovalTierRowRef = { touchAll: () => void };

type ApprovalTierRowProps = {
  index: number;
  tier: ApprovalTier;
  isFirstTier: boolean;
  currency: string;
  disabled?: boolean;
  onChange: (index: number, tier: ApprovalTier) => void;
  onValidityChange?: (index: number, isValid: boolean) => void;
  onFocus?: () => void;
  crossTierError?: string;
  ref?: Ref<ApprovalTierRowRef>;
};

function parseAmount(val: string): number {
  const s = sanitizeToNumeric(val);
  return parseFloat(s || '0') || 0;
}

function ApprovalTierRow({
  index,
  tier,
  isFirstTier,
  currency,
  disabled = false,
  onChange,
  onValidityChange,
  onFocus,
  crossTierError,
  ref,
}: ApprovalTierRowProps) {
  const { t } = useTranslation('translations', {
    keyPrefix: 'TransactionApproval.Configuration.Workflows.Update.ApprovalTierRow',
  });

  const config = useMemo(
    (): FieldData<TierValues>[] => [
      {
        fieldName: 'startAmount',
        showField: () => true,
        getInputState: () => (!isFirstTier || disabled ? 'disabled' : 'optional'),
        validate: () =>
          isFirstTier
            ? Yup.string().required(t('Validation.startAmountRequired', { defaultValue: 'Start amount is required' }))
            : Yup.string(),
        renderField: ({ getFormikData }) => (
          <TextField
            {...getFormikData<string>({ onFocus })}
            preset="currency"
            variant="outlineShadow"
            placeholder={t('enterAmount', { defaultValue: 'Enter Amount' })}
            elementLeft={
              <span className={`${styles.currencyPrefix}${!isFirstTier || disabled ? ` ${styles.disabled}` : ''}`}>
                {isFirstTier ? (
                  currency
                ) : (
                  <>
                    <span style={{ position: 'relative', top: '-1px', marginRight: '5px' }}>&gt;</span>
                    {currency}
                  </>
                )}
              </span>
            }
            disabled={!isFirstTier || disabled}
          />
        ),
      },
      {
        fieldName: 'endAmount',
        showField: () => true,
        getInputState: () => (disabled || tier.noUpperLimit ? 'disabled' : 'required'),
        validate: (ctx) =>
          tier.noUpperLimit
            ? Yup.string()
            : Yup.string()
                .required(t('Validation.endAmountRequired', { defaultValue: 'End amount is required' }))
                .test(
                  'greater-than-start',
                  t('Validation.endAmountGreaterThanStart', { defaultValue: 'Must be greater than start amount' }),
                  function (val) {
                    const start = parseAmount(ctx.values.startAmount || '0');
                    const end = parseAmount(val || '0');
                    return end > start;
                  },
                ),
        renderField: ({ getFormikData }) => {
          const fieldProps = getFormikData<string>({ onFocus });
          return (
            <>
              <TextField
                {...fieldProps}
                preset="currency"
                variant="outlineShadow"
                placeholder={
                  tier.noUpperLimit
                    ? t('noLimit', { defaultValue: 'No limit' })
                    : t('enterAmount', { defaultValue: 'Enter Amount' })
                }
                elementLeft={
                  tier.noUpperLimit ? undefined : (
                    <span className={`${styles.currencyPrefix}${disabled ? ` ${styles.disabled}` : ''}`}>
                      {currency}
                    </span>
                  )
                }
                disabled={disabled || tier.noUpperLimit}
                error={fieldProps.errorMsg || crossTierError || null}
              />
              {!disabled && (
                <TierCheckbox
                  name={`tier-${index}-noUpperLimit`}
                  label={t('noUpperLimit', { defaultValue: 'This tier has no upper limit' })}
                  checked={tier.noUpperLimit}
                  disabled={disabled}
                  onChange={(checked) => {
                    onChange(index, { ...tier, noUpperLimit: checked, endAmount: checked ? '' : tier.endAmount });
                  }}
                />
              )}
            </>
          );
        },
      },
      {
        fieldName: 'approversNeeded',
        showField: () => true,
        getInputState: () => (disabled ? 'disabled' : 'required'),
        validate: () =>
          Yup.string()
            .required(t('Validation.approversRequired', { defaultValue: 'Required' }))
            .test('min-one', t('Validation.approversMinOne', { defaultValue: 'Must be at least 1' }), (val) => {
              if (!val) return false;
              const num = parseInt(val, 10);
              return !isNaN(num) && num >= 1;
            }),
        renderField: ({ getFormikData }) => (
          <TextField
            {...getFormikData<string>({ onFocus })}
            variant="outlineShadow"
            preset="numericWholePositive"
            placeholder={t('enter', { defaultValue: 'Enter' })}
            disabled={disabled}
          />
        ),
      },
    ],
    [isFirstTier, disabled, tier.noUpperLimit, tier.endAmount, currency, t, crossTierError, index, onChange, onFocus],
  );

  const { ctx, renderFormElements } = useForm<TierValues>({
    config,
    initialValues: {
      startAmount: tier.startAmount,
      endAmount: tier.endAmount,
      approversNeeded: tier.approversNeeded,
    },
    enableReinitialize: true,
    onSubmit: () => {},
  });

  useImperativeHandle(ref, () => ({
    touchAll: () => {
      ctx.setFieldTouched('startAmount', true);
      ctx.setFieldTouched('endAmount', true);
      ctx.setFieldTouched('approversNeeded', true);
    },
  }));

  useEffect(() => {
    onValidityChange?.(index, ctx.isValid);
  }, [ctx.isValid]);

  useEffect(() => {
    if (isFirstTier && ctx.values.startAmount !== tier.startAmount) {
      onChange(index, { ...tier, startAmount: ctx.values.startAmount });
    }
  }, [ctx.values.startAmount]);

  useEffect(() => {
    const sanitizedApprovers = sanitizeToNumeric(ctx.values.approversNeeded) ?? '';
    if (ctx.values.endAmount !== tier.endAmount || sanitizedApprovers !== tier.approversNeeded) {
      onChange(index, { ...tier, endAmount: ctx.values.endAmount, approversNeeded: sanitizedApprovers });
    }
  }, [ctx.values.endAmount, ctx.values.approversNeeded]);

  return (
    <div className={styles.row}>
      <TierBadge index={index} />

      <div className={styles.amountRange}>
        <div className={styles.amountFields}>
          <div className={styles.startAmountField}>
            {renderFormElements({ config: [config[0]], override: (fd) => fd.template })}
          </div>
          <MaterialSymbol className={styles.arrow} icon="arrow_forward" />
          <div className={styles.endAmountColumn}>
            {renderFormElements({ config: [config[1]], override: (fd) => fd.template })}
          </div>
        </div>
      </div>

      <div className={styles.approversCell}>
        {renderFormElements({ config: [config[2]], override: (fd) => fd.template })}
      </div>
    </div>
  );
}

export default ApprovalTierRow;
